import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Check,
  ArrowRight,
  Send,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import MobileInput from "../../Components/Common/MobileInput";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import { Label } from "../../Components/UI/label";
import externalAPI from "../../Connect/ExternalAPI";
import { checkIfExists } from "../../Services/LoginAPI";
import {
  getImageRoot,
  isLive
} from "../../Utils/Utilities";
import useIsMobile from "../../Hooks/useIsMobile";

const STORE_LINKS = {
  android: "https://play.google.com/store/apps/details?id=com.sahicrm.androidapp",
  ios: "https://apps.apple.com/app/id6745503602",
};

const BRAND_DARK = "#0B1B2B";
const GA_TRACKING_ID = "G-CCT7V28F26";

const initGA4 = () => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
  }

  if (!document.querySelector(`script[src*="${GA_TRACKING_ID}"]`)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
    document.head.appendChild(script);
  }

  const allowedHosts = ["sahicrm.com", "sahi.club", "localhost"];
  const currentHost = window.location.hostname.toLowerCase();
  if (allowedHosts.some((h) => currentHost === h || currentHost.endsWith("." + h))) {
    window.gtag("config", GA_TRACKING_ID, {
      page_path: window.location.pathname,
      page_title: "Start - SahiClub"
    });
  }
};

const trackGA4Event = (eventName, params = {}) => {
  try {
    if (typeof window !== "undefined") {
      initGA4();
      if (typeof window.gtag === "function") {
        window.gtag("event", eventName, params);
      }
    }
  } catch (err) {
    console.warn("GA4 event tracking error:", err);
  }
};

const validateEmail = (email) => {
  if (!email) return true; // Optional field
  const regex = /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
  return regex.test(email);
};

const isAppleMobileDevice = () => {
  if (typeof window === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

export default function Start() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [step, setStep] = useState("FORM"); // 'CHECKING' | 'FORM' | 'DOWNLOAD'
  const [submitting, setSubmitting] = useState(false);
  const [isIOS] = useState(() => isAppleMobileDevice());
  const [hasTrackedFormFill, setHasTrackedFormFill] = useState(false);

  // Parse URL Parameters (Meta ads lead parameters)
  const iParam = searchParams.get("i") || "";
  let initialMobile = searchParams.get("mobile") || "";
  let initialCC = searchParams.get("cc") || "91";

  if (iParam) {
    const parts = iParam.split("_");
    if (parts.length === 2) {
      initialCC = parts[0];
      initialMobile = parts[1];
    }
  }

  const initialName = searchParams.get("name") || "";
  const initialCompany = searchParams.get("company") || searchParams.get("bizName") || "";
  const initialEmail = searchParams.get("email") || "";
  const rawSrc = searchParams.get("src") || searchParams.get("source") || searchParams.get("utm_source") || "Meta Ads";

  const [formData, setFormData] = useState({
    mobile: initialMobile,
    countryCode: initialCC,
    name: initialName,
    companyName: initialCompany,
    email: initialEmail,
    src: rawSrc
  });

  // Track Form Fill once per session/visit
  const trackFormFillOnce = (fieldName) => {
    if (!hasTrackedFormFill) {
      setHasTrackedFormFill(true);
      const platform = isIOS ? "iOS" : "Android";
      const storeName = isIOS ? "App Store" : "Play Store";
      trackGA4Event("form_fill", {
        form_name: "start_lead_form",
        first_field: fieldName,
        platform: platform,
        store_name: storeName,
        source: rawSrc
      });
    }
  };

  // Redirect desktop users to /Web & Track Desktop Visit/Redirect in GA4
  useEffect(() => {
    if (!isMobile) {
      trackGA4Event("start_desktop_redirect", {
        event_category: "Redirection",
        platform: "Desktop",
        source: rawSrc,
        page_location: window.location.href,
        destination: "/Web"
      });
      trackGA4Event("page_view", {
        page_title: "Start (Desktop Redirect) - SahiClub",
        page_location: window.location.href,
        page_path: window.location.pathname,
        platform: "Desktop",
        source: rawSrc
      });

      const timer = setTimeout(() => {
        window.location.replace("/Web");
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [isMobile, rawSrc]);

  // Track Mobile Page Visit in GA4
  useEffect(() => {
    if (isMobile) {
      const platform = isIOS ? "iOS" : "Android";
      const storeName = isIOS ? "App Store" : "Play Store";

      // 1. GA4 Page Visit Event (Intent to know OS & platform)
      trackGA4Event("page_view", {
        page_title: "Start - SahiClub",
        page_location: window.location.href,
        page_path: window.location.pathname,
        platform: platform,
        store_name: storeName,
        source: rawSrc
      });

      trackGA4Event("start_page_visit", {
        platform: platform,
        store_name: storeName,
        source: rawSrc
      });
    }
  }, [isMobile, isIOS, rawSrc]);

  // Check user/lead status and autofill info when mobile number is valid (e.g. 10 digits for India)
  useEffect(() => {
    const cleanMobile = formData.mobile ? formData.mobile.replace(/\D/g, "") : "";
    const cc = formData.countryCode || "91";

    const isComplete = (cc === "91" && cleanMobile.length === 10)
    if (!isComplete) {
      return;
    }

    let isCancelled = false;

    const performCheck = async () => {
      try {
        const result = await checkIfExists(cleanMobile, cc);
        if (isCancelled) return;

        // Fill relevant data if available in Table1 only if fields are currently empty
        if (result?.Table1?.length > 0) {
          const info = result.Table1[0];
          setFormData((prev) => ({
            ...prev,
            name: prev.name && prev.name.trim() ? prev.name : (info.Name || ""),
            companyName: prev.companyName && prev.companyName.trim() ? prev.companyName : (info.CompanyName || ""),
            email: prev.email && prev.email.trim() ? prev.email : (info.Email || "")
          }));
        }
      } catch (err) {
        console.warn("checkIfExists error:", err);
      }
    };

    performCheck();

    return () => {
      isCancelled = true;
    };
  }, [formData.mobile, formData.countryCode]);

  // Smoothly scroll field into view when focused (to prevent virtual keyboard obstruction on mobile)
  const handleFieldFocus = (e) => {
    const target = e?.target;
    if (!target) return;
    setTimeout(() => {
      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      });
    }, 280);
  };

  // Handle Form Submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (submitting) return;

    // 1. Validate Mobile
    const cleanMobile = formData.mobile.replace(/\D/g, "");
    if (!cleanMobile) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (formData.countryCode === "91" && cleanMobile.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (formData.countryCode !== "91" && (cleanMobile.length < 5 || cleanMobile.length > 15)) {
      toast.error("Please enter a valid mobile number.");
      return;
    }

    // 2. Validate Name & Company
    if (!formData.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!formData.companyName.trim()) {
      toast.error("Please enter your business/company name.");
      return;
    }

    // 3. Validate Email (if provided)
    if (formData.email && !validateEmail(formData.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);

    const platform = isIOS ? "iOS" : "Android";
    const storeName = isIOS ? "App Store" : "Play Store";

    // 3. Button Click (intent: app download) & 4. Store/OS Choice Tracking (GA4 & Meta Pixel)
    trackGA4Event("app_store_click", {
      event_category: "App Download",
      store_name: storeName,
      platform: platform,
      intent: "app_download",
      source: formData.src || "Meta Ads"
    });

    trackGA4Event("start_lead_submit", {
      store_name: storeName,
      platform: platform,
      intent: "app_download",
      source: formData.src || "Meta Ads",
      has_email: Boolean(formData.email)
    });

    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "Lead", {
        content_name: storeName,
        content_category: "App Download",
        platform: platform
      });
      window.fbq("trackCustom", "DownloadAppClick", {
        store: storeName,
        platform: platform
      });
    }

    try {
      // Save as Lead under AgencyID = 1 (Biziverse/SahiClub)
      const leadPayload = {
        agencyID: 1, // Biziverse / SahiClub Host Agency
        company: formData.companyName.trim(),
        name: formData.name.trim(),
        mobile: cleanMobile,
        cc: formData.countryCode || "91",
        email: formData.email ? formData.email.trim() : "",
        product: "SahiClub App",
        needs: "Meta Lead - App Onboarding",
        source: formData.src || "Meta Ads",
        agencyName: "SahiClub",
        ownerMobile: "",
        ownerName: "SahiClub Team",
        ownerCC: "91",
        calledFrom: "STARTPAGE"
      };

      await externalAPI.sendWebstoreInquiry(leadPayload);

      // Store in localStorage for reference
      localStorage.setItem("StartLead", JSON.stringify({
        name: formData.name.trim(),
        company: formData.companyName.trim(),
        mobile: cleanMobile,
        cc: formData.countryCode,
        email: formData.email.trim(),
        savedDT: new Date().toISOString()
      }));

      // Directly redirect to App Store or Play Store
      window.location.href = targetStoreUrl;
    } catch (error) {
      console.error("Error submitting lead:", error);
      // Even on API issue, redirect the visitor to app store
      window.location.href = targetStoreUrl;
    } finally {
      setSubmitting(false);
    }
  };

  const targetStoreUrl = isIOS ? STORE_LINKS.ios : STORE_LINKS.android;

  // If on desktop, prevent rendering mobile view while redirect takes place
  if (!isMobile) {
    return null;
  }

  // Render Checking State
  if (step === "CHECKING") {
    return (
      <div className="min-h-[100dvh] w-full bg-white flex flex-col items-center justify-center p-6 select-none font-sans">
        <Loader2 className="w-9 h-9 text-sahi-green animate-spin mb-3" />
        <p className="text-sm font-semibold text-zinc-600">Setting up your profile...</p>
      </div>
    );
  }

  // Render App Download Screen
  if (step === "DOWNLOAD") {
    return (
      <div className="min-h-[100dvh] w-full !bg-[#f1f1f0] flex flex-col justify-between px-6 py-8 sm:py-10 max-w-full mx-auto font-sans select-none antialiased">
        {/* Top Content */}
        <div className="w-full flex flex-col space-y-6 pt-2">
          {/* Badge: Account Created */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 pl-1 rounded-full bg-sahi-green-extra-light text-sahi-green text-xs sm:text-sm font-semibold w-fit border border-sahi-green/20 scale-110">
            <span className="w-4 h-4 rounded-full bg-sahi-green-light flex items-center justify-center">
              <Check className="w-2.5 h-2.5 text-sahi-green stroke-[3.5]" />
            </span>
            <span>Profile Created</span>
          </div>

          {/* Headline & Subtitle */}
          <div className="space-y-2 text-left">
            <h1
              className="text-[28px] sm:text-[32px] font-extrabold leading-[1.2] tracking-tight"
              style={{ color: BRAND_DARK }}
            >
              Get the app to set up <br />
              your business
            </h1>
            <p className="text-[15px] sm:text-[16px] text-[#64748b] leading-relaxed">
              This takes about a minute. You'll pick up right where you left off.
            </p>
          </div>

          {/* App Icon Card */}
          <div className="w-full flex items-center justify-center py-4 sm:py-6">
            <div className="w-32 h-32 sm:w-36 sm:h-36 rounded-[28px] sm:rounded-[32px] bg-gradient-to-b from-white to-[#f8faf9] border border-zinc-200/80 shadow-2xl shadow-zinc-300/90 flex items-center justify-center p-5 relative overflow-hidden transition-transform duration-200 hover:scale-[1.02]">
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-white pointer-events-none" />
              <img
                src={getImageRoot("Images", "SahiCRM_Short_dark.png")}
                alt="SahiClub"
                className="w-20 h-20 sm:w-24 sm:h-24 object-contain drop-shadow-xs relative z-10"
              />
            </div>
          </div>

          {/* 3 Features / Value Points */}
          <div className="w-full flex justify-center pt-4 sm:pt-8 pb-2">
            <div className="w-fit flex flex-col space-y-3.5">
              <div className="flex items-center gap-3.5">
                <span className="w-6 h-6 rounded-full bg-sahi-green-light flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-sahi-green stroke-[3.5]" />
                </span>
                <span
                  className="text-[16px] sm:text-[17px] font-semibold tracking-tight"
                  style={{ color: BRAND_DARK }}
                >
                  Increase Sales conversion
                </span>
              </div>

              <div className="flex items-center gap-3.5">
                <span className="w-6 h-6 rounded-full bg-sahi-green-light flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-sahi-green stroke-[3.5]" />
                </span>
                <span
                  className="text-[16px] sm:text-[17px] font-semibold tracking-tight"
                  style={{ color: BRAND_DARK }}
                >
                  Smartify Team & Tasks
                </span>
              </div>

              <div className="flex items-center gap-3.5">
                <span className="w-6 h-6 rounded-full bg-sahi-green-light flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-sahi-green stroke-[3.5]" />
                </span>
                <span
                  className="text-[16px] sm:text-[17px] font-semibold tracking-tight"
                  style={{ color: BRAND_DARK }}
                >
                  Enjoy Stress-free Management
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA Button */}
        <div className="w-full pt-8 pb-2">
          <Button
            asChild
            variant="sahiGreen"
            size="lg"
            className="w-full h-12 font-bold text-[16px] sm:text-[17px] rounded-xl shadow-md no-underline cursor-pointer flex items-center justify-center gap-2"
          >
            <a href={targetStoreUrl}>
              <span>{isIOS ? "Continue to App Store" : "Continue to Play Store"}</span>
              <ArrowRight className="w-4.5 h-4.5 stroke-[2.5]" />
            </a>
          </Button>
        </div>
      </div>
    );
  }

  // Render Mobile Lead Form ('FORM')
  return (
    <div className="min-h-[100dvh] w-full bg-gradient-to-t from-[#e9f0eb] via-[#f4f7f5] to-[#fbfdfc] flex flex-col justify-start select-none antialiased font-sans px-5 sm:px-6 py-8 pb-36 sm:py-10 sm:pb-40">
      <div className="w-full max-w-sm sm:max-w-md mx-auto space-y-6">
        {/* 1. Centered Brand Logo */}
        <div className="flex justify-center pt-2">
          <img
            src={isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"}
            alt="SahiClub"
            className="h-10 sm:h-12 w-auto object-contain"
          />
        </div>

        {/* Hero Section: Headline, Divider, and Subtitle with tighter spacing to the divider */}
        <div className="space-y-3.5">
          {/* 2. Headline */}
          <div className="text-center">
            <h1 className="text-2xl sm:text-[28px] font-extrabold text-sahi-dark-blue tracking-tight leading-[1.25]">
              <span className="text-sahi-green">Exclusively</span> for<br />
              <span className="text-sahi-green">Smart</span> Businesses
            </h1>
          </div>

          {/* 3. Centered Thin Divider */}
          <div className="w-48 sm:w-56 mx-auto border-b border-gray-300/80" />

          {/* 4. Subtitle / Guidance */}
          <div className="text-center">
            <p className="text-[15px] sm:text-base font-normal text-gray-500 leading-snug">
              Please set up your profile to<br />
              <span className="font-semibold text-gray-600">Join SahiClub for Free!</span>
            </p>
          </div>
        </div>

        {/* 5. Form Fields (Layout order: Business Name -> Full Name -> Mobile Number -> Email Address) */}
        <form onSubmit={handleSubmit} onFocusCapture={handleFieldFocus} className="space-y-4 pt-2">
          {/* 1. Business Name */}
          <div className="space-y-1.5 text-left">
            <Label className="text-sm font-semibold text-gray-700">
              Business Name <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              required
              placeholder="e.g. Acme Corp"
              value={formData.companyName}
              onFocus={(e) => {
                handleFieldFocus(e);
                trackFormFillOnce("company");
              }}
              onChange={(e) => {
                trackFormFillOnce("company");
                setFormData((prev) => ({ ...prev, companyName: e.target.value }));
              }}
              className="h-11 text-sm text-[var(--text-color)] placeholder:text-gray-400 bg-white border border-gray-300 focus:border-sahi-green focus:ring-1 focus:ring-sahi-green rounded-lg shadow-2xs"
            />
          </div>

          {/* 2. Full Name */}
          <div className="space-y-1.5 text-left">
            <Label className="text-sm font-semibold text-gray-700">
              Full Name <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              required
              placeholder="e.g. John Doe"
              value={formData.name}
              onFocus={(e) => {
                handleFieldFocus(e);
                trackFormFillOnce("name");
              }}
              onChange={(e) => {
                trackFormFillOnce("name");
                setFormData((prev) => ({ ...prev, name: e.target.value }));
              }}
              className="h-11 text-sm text-[var(--text-color)] placeholder:text-gray-400 bg-white border border-gray-300 focus:border-sahi-green focus:ring-1 focus:ring-sahi-green rounded-lg shadow-2xs"
            />
          </div>

          {/* 3. Mobile Number */}
          <div className="space-y-1.5 text-left">
            <Label className="text-sm font-semibold text-gray-700">
              Mobile Number <span className="text-red-500">*</span>
            </Label>
            <MobileInput
              id="startMobileInput"
              value={formData.mobile}
              onFocus={(e) => {
                handleFieldFocus(e);
                trackFormFillOnce("mobile");
              }}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                trackFormFillOnce("mobile");
                setFormData((prev) => ({ ...prev, mobile: val }));
              }}
              placeholder="e.g. 9876543210"
              selectedCountryCode={formData.countryCode}
              onCountryCodeChange={(newCC) =>
                setFormData((prev) => ({ ...prev, countryCode: newCC }))
              }
              className="w-full"
            />
          </div>

          {/* 4. Email Address (Optional) */}
          <div className="space-y-1.5 text-left">
            <Label className="text-sm font-semibold text-gray-700">
              Email Address
            </Label>
            <Input
              type="email"
              placeholder="e.g. kevin@peach.com"
              value={formData.email}
              onFocus={(e) => {
                handleFieldFocus(e);
                trackFormFillOnce("email");
              }}
              onChange={(e) => {
                trackFormFillOnce("email");
                setFormData((prev) => ({ ...prev, email: e.target.value }));
              }}
              className="h-11 text-sm text-[var(--text-color)] placeholder:text-gray-400 bg-white border border-gray-300 focus:border-sahi-green focus:ring-1 focus:ring-sahi-green rounded-lg shadow-2xs"
            />
          </div>

          {/* Continue to Store Button */}
          <div className="pt-3">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer rounded-lg shadow-xs text-white bg-[#0b5d1e] hover:bg-[#094d19] transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Redirecting...</span>
                </>
              ) : (
                <>
                  <span>{isIOS ? "Continue to App Store" : "Continue to Play Store"}</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
